import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { LicenseStorage } from '@portal-core/license-storage/models/license-storage.model';
import { LicenseAdminSettings } from '@portal-core/licenses/models/license-admin-settings.model';
import { ContactInfo } from '@portal-core/licenses/models/license-contact-info.model';
import { LicenseCreateRequest } from '@portal-core/licenses/models/license-create-request.model';
import { LicenseSamlAuthenticationSettingsSimple } from '@portal-core/licenses/models/license-saml-authentication-settings-simple.model';
import { LicenseSamlAuthenticationSettings } from '@portal-core/licenses/models/license-saml-authentication-settings.model';
import { LicenseOpenAISettings } from '@portal-core/licenses/models/license-open-ai-settings.model';
import { OpenAICompletion } from '@portal-core/project-files/models/open-ai/open-ai-completion.model';
import { LicenseSeatCounts } from '@portal-core/licenses/models/license-seat-counts.model';
import { SecuritySettings } from '@portal-core/licenses/models/license-security-settings.model';
import { LicenseSettings } from '@portal-core/licenses/models/license-settings.model';
import { SubscriptionAddition } from '@portal-core/licenses/models/license-subscription-addition.model';
import { SubscriptionOrderItem } from '@portal-core/licenses/models/license-subscription-order-item.model';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensePublicModel } from '@portal-core/licenses/models/license-public.model';
import { LicensesSizeStorage } from '@portal-core/licenses/models/licenses-size-storage.model';
import { UserBulkResponse } from '@portal-core/users/models/user-bulk-response.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable } from 'rxjs';

@Service()
export class LicensesApiService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  @SharePendingRequest()
  getLicensesForCurrentUser$(): Observable<License[]> {
    return this.http.get<License[]>(`${this.apiService.centralApiBaseUri}/api/licenses/My/SimplifiedModel`);
  }

  createLicense$(licenseCreateRequest: LicenseCreateRequest): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/licenses/Create`, licenseCreateRequest);
  }

  @SharePendingRequest()
  getLicensesByQuery$(filter: PageFilter): Observable<Page<License>> {
    return this.http.post<Page<License>>(`${this.apiService.centralApiBaseUri}/api/licenses/query`, filter);
  }

  @SharePendingRequest()
  getLicenseById$(licenseId: number | string): Observable<License> {
    return this.http.get<License>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}`);
  }

  @SharePendingRequest()
  getLicenseDefaultTeams$(licenseId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/DefaultTeams`);
  }

  verifyBulkUserCsvFile$(licenseId: number, seatType: number, csvFile: FormData): Observable<UserBulkResponse> {
    return this.http.post<UserBulkResponse>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/bulkUsers?seatType=${seatType}`, csvFile);
  }

  @SharePendingRequest()
  getStorageInfo$(licenseId: number): Observable<LicenseStorage> {
    return this.http.get<LicenseStorage>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/storageInfo`);
  }

  setActivationStatus$(licenseId: number, status: number): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/licenses/ChangeActivationStatus`, { licenseId, status });
  }

  changeMaxCountOfSeats$(licenseId: number, licensesSeats: LicenseSeatCounts): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/changeMaxCountOfSeats`, licensesSeats);
  }

  changeMaxStorageSize$(licenseId: number, licensesSize: LicensesSizeStorage): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/changeMaxStorageSize`, licensesSize);
  }

  changeLicenseSettings$(licenseId: number, licenseData: LicenseSettings, avatar: File): Observable<MadCloudResult<void>> {
    const formData = new FormData();
    formData.append('LicenseSettings', licenseData ? JSON.stringify(licenseData) : null);

    if (typeof avatar !== 'undefined') {
      formData.append('AvatarFile', avatar);
    }

    formData.append('IsAvatarSet', JSON.stringify(avatar !== null ? true : false));

    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/changeLicenseSettings`, formData);
  }

  changeVanityUrl$(licenseId: number, vanityUrl: string): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/ChangeVanityUrl`, { VanityBaseUrl: vanityUrl });
  }

  changeAdminLicenseSettings$(licenseId: number, licenseData: LicenseAdminSettings, avatar: File): Observable<MadCloudResult<void>> {
    const formData = new FormData();
    formData.append('LicenseData', licenseData ? JSON.stringify(licenseData) : null)
    if (typeof avatar !== 'undefined') {
      formData.append('AvatarFile', avatar);
    }

    formData.append('IsAvatarSet', JSON.stringify(avatar !== null ? true : false));
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/changeAdminLicenseSettings`, licenseData);
  }

  emailMadCapSalesTeam$(licenseId: number, message: string): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/emailMadCapSalesTeam`, JSON.stringify(message),
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        })
      });
  }

  calculateSubscriptionAdditions$(licenseId: number, additions: SubscriptionAddition): Observable<SubscriptionOrderItem[]> {
    return this.http.post<SubscriptionOrderItem[]>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/calculateSubscriptionAdditions`, additions);
  }

  processSubscriptionAdditions$(licenseId: number, additions: SubscriptionAddition): Observable<void> {
    return this.http.post<void>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/processSubscriptionAdditions`, additions);
  }

  @SharePendingRequest()
  getContactInfo$(licenseId: number): Observable<ContactInfo> {
    return this.http.get<ContactInfo>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/contactInfo`);
  }

  deleteContactInfo$(licenseId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/removeContactInfo`);
  }

  updateContactInfo$(licenseId: number, contactInfoModel: ContactInfo): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/updateContactInfo`, contactInfoModel);
  }

  changeSecuritySettings$(licenseId: number, securitySettingsModel: SecuritySettings): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/ChangeSecuritySettings`, securitySettingsModel);
  }

  validateUserEmailAddress$(licenseId: number, email: string): Observable<MadCloudResult<void>> {
    return this.http.post<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/singleUser?email=${encodeURIComponent(email)}`, null);
  }

  @SharePendingRequest()
  getOpenAISettings$(licenseId: number): Observable<LicenseOpenAISettings> {
    return this.http.get<LicenseOpenAISettings>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/openAISettings`);
  }

  changeOpenAISettings$(licenseId: number, apiKey: string, apiModel: string): Observable<MadCloudResult<void>> {
    return this.http.put<MadCloudResult<void>>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/changeOpenAISettings`, { ApiKey: apiKey, ApiModel: apiModel });
  }

  verifyOpenAIKey$(apiKey: string, apiModel: string): Observable<MadCloudResult<OpenAICompletion>> {
    return this.http.post<MadCloudResult<OpenAICompletion>>(`${this.apiService.centralApiBaseUri}/api/licenses/verifyOpenAIKey`, { ApiKey: apiKey, ApiModel: apiModel });
  }

  setSamlAuthenticationSettings$(licenseId: number, licenseSamlAuthenticationSettings: LicenseSamlAuthenticationSettings): Observable<void> {
    return this.http.put<void>(`${this.apiService.centralApiBaseUri}/api/licenses/${licenseId}/samlAuthenticationSettings`, licenseSamlAuthenticationSettings);
  }

  @SharePendingRequest()
  getSamlAuthenticationSettings$(licenseId: number): Observable<LicenseSamlAuthenticationSettingsSimple> {
    // GET /api/licenses/samlAuthenticationSettings returns the base LicenseSamlAuthenticationSettingsSimpleModel
    // ({ LicenseId, SignInButtonLabel, Enabled }) — the full settings come from getSamlAuthenticationSettingsAndDefaultTeams$.
    let path = `${this.apiService.centralApiBaseUri}/api/licenses/samlAuthenticationSettings`;
    if (licenseId) {
      path += `?licenseId=${licenseId}`;
    }
    return this.http.get<LicenseSamlAuthenticationSettingsSimple>(path);
  }

  @SharePendingRequest()
  getSamlAuthenticationSettingsAndDefaultTeams$(licenseId: number): Observable<LicenseSamlAuthenticationSettings> {
    let path = `${this.apiService.centralApiBaseUri}/api/licenses/samlAuthenticationSettingsAndDefaultTeams`;
    if (licenseId) {
      path += `?licenseId=${licenseId}`;
    }
    return this.http.get<LicenseSamlAuthenticationSettings>(path);
  }

  @SharePendingRequest()
  getLicenseByDomain$(): Observable<LicensePublicModel> {
    return this.http.get<LicensePublicModel>(`${this.apiService.centralApiBaseUri}/api/licenses/getDomainLicense`);
  }
}
